import { useState, useCallback, useRef, useEffect } from 'react';

import { Box, Container, Stack } from '@mui/material';
import cx from 'classnames';
import { Outlet, useLocation } from 'react-router';

import styles from './DefaultLayout.module.scss';
import { NewProjectModal } from '@/components/global/NewProjectModal';
import { Sidebar } from '@/components/global/Sidebar/Sidebar';
import { SkipLinks } from '@/components/global/SkipLinks';
import { TopNavigation } from '@/components/global/TopNavigation';
import { useHasNewProjectManagement } from '@/hooks/featureFlags';
import { useNavigationStore } from '@/stores/navigation';

interface DefaultLayoutProps {
  isLegacyRoute?: boolean;
}

export const DefaultLayout = ({
  isLegacyRoute = false,
}: DefaultLayoutProps) => {
  const { isCollapsed } = useNavigationStore();
  const { pathname } = useLocation();
  const mainElementRef = useRef<HTMLElement>(null);
  const hasNewProjectManagement = useHasNewProjectManagement();

  // Add focus to main content after route change
  useEffect(() => {
    if (mainElementRef.current) {
      mainElementRef.current.focus();
    }
  }, [pathname]);

  // Store callback refs from SkipLinks
  // These will be set when SkipLinks notifies us that its callback refs are ready
  const [mainContentCallbackRef, setMainContentCallbackRef] = useState<
    ((element: HTMLElement | null) => void) | null
  >(null);
  const [navigationCallbackRef, setNavigationCallbackRef] = useState<
    ((element: HTMLElement | null) => void) | null
  >(null);

  // Stable callback functions to prevent unnecessary re-renders
  // These functions will be called by SkipLinks to provide us with its callback refs
  const handleMainContentRefReady = useCallback(
    (ref: (element: HTMLElement | null) => void) => {
      setMainContentCallbackRef(() => ref);
    },
    [],
  );

  const handleNavigationRefReady = useCallback(
    (ref: (element: HTMLElement | null) => void) => {
      setNavigationCallbackRef(() => ref);
    },
    [],
  );

  return (
    <Container>
      <SkipLinks
        onMainContentRefReady={handleMainContentRefReady}
        onNavigationRefReady={handleNavigationRefReady}
      />
      <Stack direction="row">
        <Box className={styles.LayoutWrapper}>
          <TopNavigation />
          <Stack direction="row">
            <Sidebar
              ref={(element) => {
                // Call the callback ref from SkipLinks if available
                navigationCallbackRef?.(element);
              }}
            />
            <Box
              className={cx(styles.PageWrapper, {
                [styles.IsCollapsed]: isCollapsed,
              })}
              // Used for scroll targeting for infinite scroll in new navigation
              data-scroll-container
            >
              {isLegacyRoute ? (
                <div
                  tabIndex={-1}
                  ref={(element) => {
                    // Store for our own use for focusing on navigation
                    mainElementRef.current = element;
                    // This notifies SkipLinks when the main content element is ready
                    mainContentCallbackRef?.(element);
                  }}
                  className={cx(
                    styles.PageWrapperInner,
                    styles.PageWrapperInnerLegacy,
                  )}
                >
                  <Outlet />
                </div>
              ) : (
                <main
                  tabIndex={-1}
                  ref={(element) => {
                    // Store for our own use for focusing on navigation
                    mainElementRef.current = element;
                    // This notifies SkipLinks when the main content element is ready
                    mainContentCallbackRef?.(element);
                  }}
                  className={styles.PageWrapperInner}
                >
                  <Outlet />
                </main>
              )}
            </Box>
          </Stack>
        </Box>
      </Stack>
      {hasNewProjectManagement && <NewProjectModal />}
    </Container>
  );
};
